import {EX} from "./exclaim";
import {hasValue, notSet, toBoolean} from "./ValueUtils";
import {addCsrfTokenToRequestOptions} from "./CsrfTokenManagement";
import {alertDialog} from "./jquery-exclaim-util.js";

export interface ErrorPayload {
    errorMessageId?: string;
    error_message_id?: string;
    error_id?: string;
    errorMessage?: string;
    error_message?: string;
    error?: string;

    // This is a UUID used to identify the error in server logs, not the error message resource string id
    errorId?: string;
}

export type MethodName = "GET" | "HEAD" | "POST" | "PUT" | "PATCH" | "DELETE";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type JsonReviverFunction = (key: string, value: any) => any;

export interface RequestParams extends RequestInit {
    // Extended properties we will remove before "passing along"
    url: string;
    data?: object;
    jsonBodyObj?: object;
    urlParamsBodyObj?: object;
    urlParamsObj?: object;
    showErrorDialog?: boolean;
    jsonReviver?: JsonReviverFunction;
    // Properties from the base class / interface
    method?: MethodName; // force to a typed values for our usage
    headers?: Headers; // force the typing to Headers for our usage
}

export interface ResponseDetails<TData = unknown> {
    data: TData | string;
    response: Response;
    responseText: string;
}

// exported for unit testing only
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getErrorMessageFromJsonPayload(obj: ErrorPayload): string {
    if (!obj) {
        return undefined;
    }
    // Prefer using a message id over message text (previously the priority was opposite)
    // noinspection JSUnresolvedReference
    const msg_id = obj.errorMessageId || obj.error_message_id || obj.error_id;
    if (msg_id && EX.Messages.has(msg_id)) {
        return EX.Messages.prop(msg_id);
    }
    // noinspection JSUnresolvedReference
    const msg = obj.errorMessage || obj.error_message || obj.error;
    if (msg) {
        return msg;
    }
    return undefined;
}

export function sanitizeExceptionText(text: string): string {
    if (!text) {
        return text;
    }
    // Note that the error message text usually processed by this function
    // doesn't usually receive a stacktrace; typically it just receives only
    // the exception message itself.
    // However, this regex will match the "   at ...." lines in any provided
    // text and remove them as well, as a safeguard.
    const exceptionTextFilterRegEx
        = /^\s*nested exception is:\s*$|\b[a-z]\w*(?:\.[a-z]\w*)*exception:\s+|^\s+at\s.*$/giu;
    const linesWithOneCharacterOnThemRegEx = /^.?$/u;
    const blankLinesRegEx = /^\s*$/u;
    return text
        // Split into lines
        .split(/\r\n?|\n/u)
        // Filter out the exception details we don't want to present
        .map(line => line.replace(exceptionTextFilterRegEx, ''))
        // Remove lines with only one character on them
        .filter(line => !linesWithOneCharacterOnThemRegEx.test(line))
        // Remove lines that are blank
        .filter(line => !blankLinesRegEx.test(line))
        // Trim leading and trailing whitespace
        .map(line => line.trim())
        // Filter out any lines that are a duplicate of the one before
        .filter((line, index, lines) => {
            if (index <= 0) {
                return true;
            }
            return lines[index - 1] !== line;
        })
        // Join things back up into a multi-line string
        .join("\r\n");
}

export function getResponseFailErrorMessage(status: number, payload: string): string {
    let json = null;
    try {
        if (typeof payload === "string" && payload.trim() !== "") {
            json = JSON.parse(payload);
        }
    } catch (_err) {
        /* ignore JSON parse errors as it may not be a JSON response */
    }
    return getServerRequestErrorMessage(
        status,
        undefined, // for jQuery
        payload,
        json,
    );
}

function appendErrorId(msg: string, payloadJson?: unknown) {
    const errorPayload = payloadJson as ErrorPayload;
    const errorId = errorPayload?.errorId;
    if (errorId && !msg.includes(errorId.toString())) {
        // Append the error id to the message (if we have one, and it's not already part of the message)
        return msg + EX.Messages.prop("Error_ErrorIdSuffix", errorId);
    }
    return msg;
}

function getServerRequestErrorMessageInternal(
    status: number,
    textStatus?: string,
    payloadText?: string,
    payloadJson?: unknown,
) {
    // Check if we received a JSON payload with an "errorMessage" or
    // "error" field that is a string. If we did, use that as the error
    // message to present to the user.
    const msg = getErrorMessageFromJsonPayload(payloadJson as ErrorPayload);
    if (msg && typeof msg === "string") {
        return msg;
    }

    const cleanText = payloadText ? sanitizeExceptionText(payloadText) : "";

    switch (status) {
        case 0:
            return EX.Messages.prop("Error_Not_Connected");
        case 400:
            return EX.Messages.prop("Error_Bad_Request");
        case 401:
            return EX.Messages.prop("Error_Unauthorized");
        case 403:
            return EX.Messages.prop("Error_Forbidden");
        case 404:
            return EX.Messages.prop("Error_Not_Found");
        case 503:
            return EX.Messages.prop("Error_Service_Unavailable");
        case 504:
            return EX.Messages.prop("Error_Gateway_Timeout");
        case 500:
            // Allow reasonable responses (and not stack traces) to be displayed
            if (cleanText && cleanText.length < 120) {
                return EX.Messages.prop("Error_Internal_Server_Error") + "\n\n" + cleanText;
            }
            return EX.Messages.prop("Error_Internal_Server_Error");
    }

    // This textStatus is really specific to jQuery
    // noinspection SpellCheckingInspection
    switch (textStatus) {
        case 'parsererror':
            return EX.Messages.prop("Error_Parsing");
        case 'timeout':
            return EX.Messages.prop("Error_Timeout");
        case 'abort':
            return EX.Messages.prop("Error_Aborted");
    }

    return EX.Messages.prop("Error_Unexpected", cleanText);
}

export function getServerRequestErrorMessage(
    status: number,
    textStatus?: string,
    payloadText?: string,
    payloadJson?: unknown
) {
    return appendErrorId(
        getServerRequestErrorMessageInternal(status, textStatus, payloadText, payloadJson),
        payloadJson
    );
}

/**
 * Converts the provided object in `val` to a URL-encoded search parameter string prefixed
 * with the supplied prefix (if `val` has a value).
 *
 * @param val An object to encoded as a URL-encoded search parameter string.
 * @param prefix A string to prepend to the resulting output.
 * @returns Returns a URL-encoded search parameter string. If no value was provided,
 * `undefined` or `null`, an empty string, `""`, will be returned without the prefix prepended.
 */
export function paramsObjAsUrlParams(val: object, prefix?: string): string {
    if (notSet(val)) {
        return "";
    }
    if (typeof val !== "object") {
        throw new Error("val must be an object");
    }

    const paramString = Object
        .entries(val)
        // Remove non-set values (otherwise we'll coerce them incorrectly as strings)
        .filter(([_key, value]) => hasValue(value))
        .map(([key, value]) => encodeURIComponent(key) + "=" + encodeURIComponent(value))
        .join("&");
    if (paramString === "") {
        return "";
    }
    return (hasValue(prefix) ? String(prefix) : "") + paramString;
}

export function ensureHeadersCreated(params: RequestInit) {
    if (!params.headers) {
        params.headers = new Headers();
    }
}

// Perform some validation on the parameters to help the developer
// out with the defined behaviour
function enforceValidRequestParams(params: RequestParams) {
    if (typeof params !== "object") {
        throw new Error("Need to pass all the parameters in an object");
    }
    if (typeof params.url !== "string") {
        throw new Error("The parameters must include a `url` field as a string")
    }
    if (params.data && (params.jsonBodyObj || params.urlParamsBodyObj || params.urlParamsObj)) {
        throw new Error(
            "When providing a `data` field, the default method for passing the data to the remote" +
            " server will be used. None of `jsonBodyObj`, `urlParamsBodyObj` or `urlParamsObj` should be provided."
        )
    }
    if (params.jsonBodyObj && params.urlParamsBodyObj) {
        throw new Error(
            "Invalid parameters, only one of jsonBodyObj and urlParamsBodyObject can be supplied"
        );
    }
}

/**
 * Builds the Request for a fetch() operation using the supplied parameters.
 * It will build the URL parameters or the body depending on the supplied
 * fields in the `params` parameter..
 *
 * Options can also be passed directly to `fetch()` via `params` as well.
 *
 * @param params Supplies parameters to the request building code, and to the `fetch()` operation that this calls.
 * @returns A request object ready to pass to `fetch()`.
 */
export function buildRequest(params: RequestParams): Request {
    enforceValidRequestParams(params);

    // Build the request initialize options from the source parameters (although
    // we will remove our custom parameters if present)
    const options = { ...params };
    // Make sure the method type is in uppercase
    options.method = options.method ? String(options.method).toUpperCase() as MethodName : "GET";

    // Move the URL into a local as fetch() doesn't accept the URL in the request init structure
    let url = options.url;
    delete options.url;

    // Make sure we have a headers object as we will need to add headers here
    ensureHeadersCreated(options);

    // Tell our server logic this is an "ajax request"
    options.headers.append("X-Requested-With", "XMLHttpRequest");

    if (options.data) {
        if (options.method === "GET" || options.method === "HEAD") {
            // For GET / HEAD methods, we pass the data as part of the URL.
            url = url + paramsObjAsUrlParams(options.data, "?");
        } else if (options.data instanceof FormData) {
            // Handle the case where we pass FormData in -- often used when
            // passing in file attachments from the UI.
            //
            // Do NOT set the content type, browser will do this for us
            options.body = options.data;
        } else {
            // For other methods (POST, PUT, DELETE, etc.) we pass the data
            // as a form URL encoded payload (not JSON).
            options.headers.append("Content-Type", "application/x-www-form-urlencoded");
            options.body = paramsObjAsUrlParams(options.data);
        }
        delete options.data;
    } else {
        if (options.jsonBodyObj) {
            options.headers.append("Content-Type", "application/json");
            options.body = JSON.stringify(options.jsonBodyObj);
            delete options.jsonBodyObj;
        }
        if (options.urlParamsBodyObj) {
            options.headers.append("Content-Type", "application/x-www-form-urlencoded");
            options.body = paramsObjAsUrlParams(options.urlParamsBodyObj);
            delete options.urlParamsBodyObj;
        }
        if (options.urlParamsObj) {
            url = url + paramsObjAsUrlParams(options.urlParamsObj, "?");
            delete options.urlParamsObj;
        }
    }

    addCsrfTokenToRequestOptions(options);
    return new Request(url, options as RequestInit);
}

function buildUserErrorMsg(status: number, responseText: string): string {
    const now = new Date();
    return getResponseFailErrorMessage(status, responseText) +
        '\n\n' + now.toISOString();
}

function showOptionalErrorDialog(show: boolean, msg: string) {
    if (show) {
        // Although it would be preferred to wait for the dialog to be dismissed here,
        // it prevents calling code from removing the busy indicator from the screen
        // (which blocks the user from interacting with this dialog)
        alertDialog(msg, EX.Messages.prop("Dialog_error_title"));
    }
}

function unexpectedErrorMsg(err?: Error): string {
    return EX.Messages.prop("Error_Unexpected", err?.toString());
}

/**
 * This method processes the return value from `fetch()` (or our `performFetch()` method) and
 * handles converting the payload to a JSON object, as well as optionally displaying error
 * dialogs for any error that might occur during the request processing.
 *
 * Note that regardless of whether an error dialog is presented to the user, this method will
 * always throw an exception on failure, and it will need to be caught or handled by the caller.
 *
 * @param responsePromise The return value from `fetch()` to wait for the response on.
 * @param showErrorDialog Indicates if error dialogs will be shown if an error occurs.
 * @param jsonReviver Optionally provides a JSON reviver function for parsing the JSON with.
 * @returns A promise returning response details.
 */
export async function processFetchJsonResponseWithErrorDialog<TData = unknown>(
    responsePromise: Promise<Response>,
    showErrorDialog: boolean,
    jsonReviver?: JsonReviverFunction,
): Promise<ResponseDetails<TData>> {
    if (!(responsePromise instanceof Promise)) {
        throw new Error("responsePromise parameter needs to be a Promise<Response> object");
    }

    // Wait for the fetch operation to return the response object
    let response;
    try {
        response = await responsePromise;
    } catch (err) {
        showOptionalErrorDialog(showErrorDialog, unexpectedErrorMsg(err));
        throw new Error("Error dispatching request", { cause: err });
    }

    // Wait for the response text to be received completely
    const responseText = await response.text();

    // Check if we got a "failed" response from the server
    if (!response.ok) {
        const msg = buildUserErrorMsg(response.status, responseText);
        showOptionalErrorDialog(showErrorDialog, msg);
        throw new Error(
            `Response from ${response.url} was status code ${response.status}, user message was: ${msg}`
        );
    }

    const contentTypeHeader = response.headers?.get("Content-Type") || "";
    const contentType = (contentTypeHeader.split(";", 2)[0] || "").trim().toLowerCase();

    // Parse the response text to JSON
    let data: TData | string;

    // Set the data value based on converting different content types
    // (otherwise the caller will have to process `responseText` directly).
    // This is NOT comprehensive and has issues if the data cannot be parsed
    // as text in the first place.
    if (contentType === "application/json") {
        // Avoid triggering parsing errors on non-strings and empty strings
        if (typeof responseText === "string" && responseText.trim() !== "") {
            try {
                data = JSON.parse(responseText, jsonReviver) as TData;
            } catch (err) {
                showOptionalErrorDialog(showErrorDialog, unexpectedErrorMsg(err));
                throw new Error("Failed to parse JSON response", {cause: err});
            }
        }
    } else if (contentType === "text/plain") {
        data = responseText;
    }

    return {
        data: data,
        response: response,
        responseText: responseText,
    };
}

export function addMethodToParams(method: MethodName, paramsObj?: RequestParams): RequestParams {
    if (typeof paramsObj !== "object") {
        throw new Error("`paramsObj` must be an object");
    }
    return {
        ...paramsObj,
        method: method,
    }
}

export function addJsonAcceptHeader(paramsObj: RequestParams) {
    ensureHeadersCreated(paramsObj);
    paramsObj.headers.append("Accept", "application/json");
}

async function performMethod<TData = unknown>(method: MethodName, paramsObj: RequestParams): Promise<TData> {
    const params = addMethodToParams(method, paramsObj)
    const showErrorDialog = toBoolean(params.showErrorDialog, true);
    const reviver = params.jsonReviver;
    // remove custom fields so we do not pass them through to fetch()
    delete params.showErrorDialog;
    delete params.jsonReviver;
    const r = await processFetchJsonResponseWithErrorDialog<TData>(
        performFetch(params),
        showErrorDialog,
        reviver
    );
    return r.data as TData;
}

/**
 * Using the `fetch()` API, performs a GET against the provided URL to obtain a JSON-based response.
 *
 * The data for the request is passed as an object in the `paramsObj` `data` field. Other options
 * can also be passed directly to `fetch()` via the `paramsObj` as well.
 *
 * @param paramsObj Supplies parameters to the request building code, and to the `fetch()` operation that this calls.
 * @returns A promise resolving to the payload converted to a JSON object; if no data was returned, the promise will resolve with `undefined`.
 */
export async function performGet<TData = unknown>(paramsObj: RequestParams): Promise<TData> {
    addJsonAcceptHeader(paramsObj);
    return await performMethod("GET", paramsObj);
}

/**
 * Using the `fetch()` API, performs a POST against the provided URL and obtain a JSON-based response.
 *
 * The data for the request is passed as an object in the `paramsObj` `data` field. Other options
 * can also be passed directly to `fetch()` via the `paramsObj` as well.
 *
 * @param paramsObj Supplies parameters to the request building code, and to the `fetch()` operation that this calls.
 * @returns A promise resolving to the payload converted to a JSON object; if no data was returned, the promise will resolve with `undefined`.
 * @see RequestParams
 */
export async function performPost<TData = unknown>(paramsObj: RequestParams): Promise<TData>  {
    addJsonAcceptHeader(paramsObj);
    return await performMethod<TData>("POST", paramsObj);
}

/**
 * Using the `fetch()` API, performs an operation against the provided URL and returns a
 * `Promise<Response>` that can be used to wait for and process the response.
 *
 * The data for the request is passed as an object in the `paramsObj` `data` field. Other options
 * can also be passed directly to `fetch()` via the `paramsObj` as well.
 *
 * @param paramsObj Supplies parameters to the request building code, and to the `fetch()` operation that this calls.
 * @returns A promise that will resolve to a `Response` object, and that can be waited on for the response.
 * @see RequestParams
 */
export async function performFetch(paramsObj: RequestParams): Promise<Response> {
    return await fetch(buildRequest(paramsObj));
}
